import { useCallback, useEffect, useState } from 'react';
import { FileSignature, Loader2, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import API from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { renderMarkdown } from '@/lib/markdown';
import DangerZone from '@/components/profile/DangerZone';

export const CGV_OUTDATED_EVENT = 'cgv-outdated';
const PARTNER_ROLES = ['seller', 'traiteur'];

/** Full-screen, non-dismissable gate shown to partners who must (re-)accept the producer terms. */
export default function MandatoryCGVOverlay() {
  const { user, refreshUser } = useAuth();
  const [forced, setForced] = useState(false);
  const [doc, setDoc] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [refusing, setRefusing] = useState(false);

  useEffect(() => {
    const onOutdated = () => setForced(true);
    window.addEventListener(CGV_OUTDATED_EVENT, onOutdated);
    return () => window.removeEventListener(CGV_OUTDATED_EVENT, onOutdated);
  }, []);

  const isPartner = !!user && PARTNER_ROLES.includes(user.role);
  const open = isPartner && (user.cgv_outdated || forced);

  useEffect(() => {
    if (!open || doc) return;
    API.get('/legal-documents/cgv_producteurs').then(r => setDoc(r.data)).catch(() => setDoc({ title: 'Conditions Générales des Producteurs', content: '' }));
  }, [open, doc]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const accept = useCallback(async () => {
    setAccepting(true);
    try {
      await API.post('/auth/accept-cgv');
      setForced(false);
      await refreshUser();
      toast.success('Merci ! Conditions acceptées.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Impossible d’enregistrer votre acceptation');
    } finally { setAccepting(false); }
  }, [refreshUser]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] bg-stone-text/80 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="mandatory-cgv-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
        <header className="px-6 md:px-8 pt-6 pb-4 border-b border-clay flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-brick/10 flex items-center justify-center shrink-0"><FileSignature className="h-5 w-5 text-brick" /></div>
          <div>
            <h2 className="font-serif text-2xl text-stone-text">Nouvelles conditions producteurs</h2>
            <p className="text-sm text-muted-foreground font-sans mt-1">
              Nos conditions ont été mises à jour{doc?.version_date ? ` le ${new Date(doc.version_date).toLocaleDateString('fr-BE')}` : ''} (notamment l’article 5 : marge, reversement et TVA).
              Pour continuer à utiliser votre espace, merci d’en prendre connaissance et de les accepter.
            </p>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-6 md:px-8 py-5" data-testid="mandatory-cgv-content">
          {!doc ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-brick" /></div>
          ) : (
            <article className="font-sans text-sm text-stone-text/85 leading-relaxed legal-content" dangerouslySetInnerHTML={{ __html: renderMarkdown(doc.content) }} />
          )}
        </div>

        <footer className="px-6 md:px-8 py-4 border-t border-clay bg-paper/60">
          {refusing ? (
            <div className="space-y-3" data-testid="mandatory-cgv-refuse-panel">
              <p className="text-sm font-sans text-stone-text flex items-start gap-2">
                <ShieldAlert className="h-4 w-4 text-brick mt-0.5 shrink-0" />
                Sans acceptation, votre compte partenaire ne peut pas rester actif. Vous pouvez fermer votre compte ci-dessous (vos notes de versement déjà émises restent dues et vous seront réglées) ou revenir en arrière.
              </p>
              <DangerZone />
              <Button variant="ghost" className="rounded-full" onClick={() => setRefusing(false)} data-testid="mandatory-cgv-back">← Revenir aux conditions</Button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <Button variant="outline" className="rounded-full border-brick/40 text-brick hover:bg-brick/5" onClick={() => setRefusing(true)} data-testid="mandatory-cgv-refuse">
                Je refuse et je ferme mon compte
              </Button>
              <Button className="bg-forest text-white rounded-full px-8 hover:bg-forest/90" onClick={accept} disabled={accepting || !doc} data-testid="mandatory-cgv-accept">
                {accepting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} J’accepte les conditions
              </Button>
            </div>
          )}
        </footer>
      </div>
    </div>
  );
}
